import logger from "./logger";
import { Analytics } from "./analytics";

interface FeedbackUserData {
  email?: string;
  recommendation: string;
  recommendationName: string;
  feedback: string;
  isMaoriPacific: boolean;
  location: string;
  district: string;
  role: string;
  practiceArea: string;
}

interface FormValues {
  email?: string;
  feedback: string;
}

export interface FeedbackFormErrors {
  email: string;
  feedback: string;
}

// Get API endpoint from environment variable or default to local development
const getApiEndpoint = () => {
  // In production, this will be set by the build process
  // For local development, this can be set via VITE_API_ENDPOINT in the .env file
  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
  
  if (apiEndpoint) {
    return `${apiEndpoint}/submit-feedback`;
  }
  
  // Fallback for local development - Update this after AWS deployment
  console.warn('VITE_API_ENDPOINT not set, using development fallback');
  return '/api/submit-feedback'; // This will need to be updated with actual AWS endpoint
};

export const postFeedback = async (formData: FeedbackUserData) => {
  try {
    Analytics.trackFeedbackSubmission({
      feedback_type: 'guideline_feedback',
      recommendation_id: formData.recommendation,
      user_role: formData.role,
      has_email: !!formData.email,
      event_category: 'form_interaction'
    });

    const apiUrl = getApiEndpoint();
    
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
     // credentials: "include", // For session cookies if needed
    });

    logger.log("POST FEEDBACK:", formData);

    const data = await response.json(); // `data` is the JSON response from the server

    if (!response.ok) {
      logger.log("ERROR from backend:", data, response);
      // Construct an error object that mimics an Axios-like error structure
      // so FeedbackForm.tsx can use error.response.data.message
      const errorToThrow: any = new Error(data.message || data.error || `Request failed with status ${response.status}`);
      errorToThrow.response = {
        data: data, // This makes the full backend JSON response available
        status: response.status,
        headers: response.headers
      };
      throw errorToThrow; // Throw the enhanced error object
    }

    logger.log("RECIEVE FEEDBACK:", data);
    logger.log("Feedback submitted:", data);

    Analytics.trackCustomEvent('feedback_success', {
      event_category: 'form_interaction',
      recommendation_id: formData.recommendation,
      user_role: formData.role
    });

    return data; // Optionally return data on success
  } catch (error) {
    // This catch block now handles:
    // 1. Network errors from fetch itself (e.g., server unreachable)
    // 2. The error thrown above if !response.ok
    logger.error("Error submitting feedback in postFeedback utility:", error);

    Analytics.trackError(
      'feedback_submission_error',
      error instanceof Error ? error.message : 'Unknown error',
      'postFeedback'
    );

    throw error; // RE-THROW THE ERROR so the caller (FeedbackForm.tsx) can catch it
  }
};

const validateForm = (values: FormValues): FeedbackFormErrors => {
  const errors: FeedbackFormErrors = {
    email: "",
    feedback: "",
  };

  if (!values.feedback?.trim()) {
    errors.feedback = "Please enter your feedback";
  }

  const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}$/;
  if (values.email && !emailRegex.test(values.email)) {
    errors.email = "Please enter a email address";
  }

  return errors;
};

export const getValidationError = (
  values: FormValues,
  field: keyof FeedbackFormErrors
): string => {
  const errors = validateForm(values);
  return errors[field];
};
